import React, {Component} from 'react';
import {bindActionCreators, Dispatch} from 'redux';
import {connect} from 'react-redux';
import memoize from 'memoize-one';

import {Toolbar as PrimeToolbar} from 'primereact/toolbar';

import {__} from '~/shared/src/utilities/messageCatalog';
import User, {View} from '~/shared/src/model/User';
import {DataStructure} from '~/shared/src/utilities/dataStructure';
import {
    ColumnConfig,
    ColumnDefinition,
    ColumnFilter,
    ColumnSorter,
    RowStyling
} from '~/shared/src/validationSchemes/modules/overview';

import {StoreState} from '~/frontend/src/store';
import {ComponentProps as ReferencePanelComponentProps} from '~/frontend/src/components/base/ItemFinder/detail/ReferencePanel';
import {
    convertSortersToSortersWithField,
    convertSortersWithFieldToSorters
} from '~/frontend/src/components/base/DataTable/utilities';
import {
    applyAllColumnsToColumns
} from '~/frontend/src/components/base/ItemFinder/overview/utilities';
import {
    addParentReferenceAttributeColumnsToColumns,
    buildPropsFromViewProps,
    getAllColumns,
    getModuleForViewSelector,
    getViews,
    remapFieldsForReferenceView,
    viewSelectorPanelPropsCompare
} from '~/frontend/src/components/base/ItemFinder/detail/ReferencePanel/utilities';
import {
    PanelProps as ViewSelectorDropdownPanelProps,
    PanelProps,
    ViewOption
} from '~/frontend/src/components/base/shared/field/ViewSelectorDropdown';
import {
    ColumnConfigMenuColumn,
    ColumnConfigMenuColumnsGroup
} from '~/frontend/src/components/base/shared/menu/ColumnConfigMenu';
import {
    ColumnSortMenuColumnsGroup,
    ColumnSortMenuColumnSorter
} from '~/frontend/src/components/base/shared/menu/ColumnSortMenu';
import {
    getToolbarComponents,
    ToolbarComponents,
    ToolbarLayoutComponents
} from '~/frontend/src/utilities/toolbars';


import * as PanelActions from '~/frontend/src/actions/panel/common';
import {SetParamAction} from '~/frontend/src/actions/panel/common/setParam';
import {SetParamsAction} from '~/frontend/src/actions/panel/common/setParams';
import * as ElusiveDataActions from '~/frontend/src/actions/elusiveData';
import {UnsetElusiveData} from '~/frontend/src/actions/elusiveData';

const Actions = Object.assign( {}, PanelActions, ElusiveDataActions ) as any;
type SetParam = <K extends keyof ReferencePanelComponentProps>( panelKey : string, paramKey : K, paramValue : ReferencePanelComponentProps[K] ) => SetParamAction;
type SetParams = ( panelKey : string, panelProps : Partial<ReferencePanelComponentProps> ) => SetParamsAction;


const defaultProps = {

    /**
     * The structure of the column data.
     */
    dataStructure: {} as DataStructure,

    /**
     * The column definition of the columns possible to show in the DataTable.
     */
    columnDefinitions: [] as ColumnDefinition[],

    /**
     * The column configuration of the columns to show in the DataTable.
     */
    columns: [] as ColumnConfig[],

    /**
     * The column sorters.
     */
    sorters: [] as ColumnSorter[],

    /**
     * Search result loaded from the backend.
     */
    search: false as boolean,

    /**
     * The current panelProps.
     *
     * Only applicable if <prop>showViewSelector</prop> is set to true.
     */
    viewSelectorPanelProps: {} as PanelProps,

    /**
     * Should the ViewSelectorDropdown shown in the toolbar.
     */
    showViewSelector: true as boolean,

    /**
     * Should the view options button shown in the toolbar.
     */
    showViewOptionsButton: true as boolean,

    /**
     * Should the column options button shown in the toolbar.
     */
    showColumnOptionsButton: true as boolean,

    /**
     * Should the column sort button shown in the toolbar.
     */
    showColumnSortButton: true as boolean,

    /**
     * Is the overview is editable.
     *
     * Only applicable if <prop>showSaveButton</prop> is set to true. Used to enable/disable him.
     */
    editable: false as boolean,

    /**
     * The CSS class name(s) to add to the component.
     */
    className: '' as string
};

type DefaultProps = typeof defaultProps;

type Props = {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    /**
     * The dataContext to access global.data with.
     */
    dataContext : string;

    /**
     * The database schema to filter data and metadata for.
     */
    schema : Schema;

    /**
     * The status to filter data for.
     */
    status : Status;

    /**
     * The timestamp to filter data at.
     */
    timestamp : string;

    /**
     * The ItemTypeName to filter data and metadata for.
     */
    itemTypeName : string;

    /**
     * The itemTypeof the item to show the references for.
     */
    parentItemTypeName : string;

    /**
     * Should default values shown for properties.
     */
    showPropertyDefaults : boolean;

    /**
     * Should the MetaModel information shown as defined (raw) <strong>or</strong> should they be processed by MessageCatalog.
     */
    rawMode : boolean;

    /**
     * Should the MetaModel information as defined additionally shown for processed information.
     * This applies in the form: &lt;processed MetaModel information&gt; (&lt;defined MetaModel information&gt;)
     *
     * Only applicable if <prop>rawMode</prop> is set to false.
     */
    showAdditionalRawNames : boolean;

    /**
     * Is the overview in editMode.
     *
     * Only applicable if <prop>editable</prop> is set to true.
     */
    editMode : boolean;

    /**
     * Should a legend bar shown.
     */
    legend : boolean;
};

type StateProps = {
    /**
     * The current user.
     *
     * Will be mapped from global state.
     */
    user? : User;
};

type ActionProps = {
    // common panel actions
    setParam : SetParam;
    setParams : SetParams;

    // elusiveData actions
    unsetElusiveData : UnsetElusiveData;
}


export type ComponentProps = Partial<DefaultProps> & Props;
type AllProps = DefaultProps & Props & StateProps & ActionProps;


/**
 * Toolbar
 */
class Toolbar extends Component<AllProps> {

    static defaultProps = defaultProps;

    /**
     * @param props
     */
    constructor( props : AllProps ) {
        super( props );

        this.getViewsForViewSelectorDropdown = memoize( this.getViewsForViewSelectorDropdown );
        this.getSelectedViewForViewSelectorDropdown = memoize( this.getSelectedViewForViewSelectorDropdown );
        this.getAllColumns = memoize( this.getAllColumns );
        this.getSortersWithField = memoize( this.getSortersWithField );

        this.onViewOptionChange = this.onViewOptionChange.bind( this );
        this.onExpandColumnsToHeaderWidth = this.onExpandColumnsToHeaderWidth.bind( this );
        this.onExpandColumnsToScreenWidth = this.onExpandColumnsToScreenWidth.bind( this );
        this.onColumnVisibilityChange = this.onColumnVisibilityChange.bind( this );
        this.onColumnSortChange = this.onColumnSortChange.bind( this );
        this.onViewSelectorSetView = this.onViewSelectorSetView.bind( this );
    }

    /**
     * The handler for view option change.
     *
     * @param {String} name
     * @param {Boolean} checked
     *
     * @private
     */
    onViewOptionChange( name : string, checked : boolean ) {
        this.props.setParam( this.props.uid, name as keyof ReferencePanelComponentProps, checked );
    }

    /**
     * The handler for expand columns to header width.
     *
     * @private
     */
    onExpandColumnsToHeaderWidth() {
        this.props.setParam( this.props.uid, 'autoLayout', true );
    }

    /**
     * The handler for expand columns to screen width.
     *
     * @private
     */
    onExpandColumnsToScreenWidth() {
        this.props.setParam( this.props.uid, 'forceFit', true );
    }

    /**
     * Handler for column visibility change.
     *
     * @param {ColumnConfigMenuColumn[]} allColumnsGrouped
     *
     * @private
     */
    onColumnVisibilityChange( allColumnsGrouped : ColumnConfigMenuColumn[] ) {
        const {
                  uid,
                  columnDefinitions,
                  columns,
                  sorters,
                  search
              }                                                  = this.props,
              newColumns                                         = applyAllColumnsToColumns( allColumnsGrouped as ColumnConfigMenuColumnsGroup[], columnDefinitions, columns ),
              newSorters                                         = ( [] as ColumnSorter[] ).concat( sorters ),
              propsToSet : Partial<ReferencePanelComponentProps> = {
                  doSearch: search ? true : false,
                  columns : newColumns
              };

        // remove sorters for removed columns
        for ( const oldColumn of columns ) {

            // column removed?
            if ( !newColumns.find( column => column.field === oldColumn.field ) ) {
                const sorterIndex = newSorters.findIndex( sorter => sorter.field === oldColumn.field );

                // sorter found?
                if ( sorterIndex !== -1 ) {
                    newSorters.splice( sorterIndex, 1 );

                    if ( !propsToSet.sorters ) {
                        propsToSet.sorters = newSorters;
                    }
                }
            }
        }

        this.props.setParams( uid, propsToSet );
    }

    /**
     * Handler for column sort change.
     *
     * @param {ColumnSortMenuColumnSorter[]} sortersWithField
     *
     * @private
     */
    onColumnSortChange( sortersWithField : ColumnSortMenuColumnSorter[] ) {
        const {
                  uid,
                  search
              }                                                  = this.props,
              propsToSet : Partial<ReferencePanelComponentProps> = {
                  doSearch: search ? true : false,
                  sorters : convertSortersWithFieldToSorters( sortersWithField, this.props.columnDefinitions )
              };

        this.props.setParams( uid, propsToSet );
    }

    /**
     * Handler for viewSelector set view.
     *
     * @param {PanelProps} panelProps
     *
     * @private
     */
    onViewSelectorSetView( panelProps : PanelProps ) {
        const {
                  uid,
                  schema,
                  status,
                  timestamp,
                  itemTypeName,
                  dataStructure,
                  user
              }                                                  = this.props,
              propsToSet : Partial<ReferencePanelComponentProps> = buildPropsFromViewProps( panelProps, dataStructure, schema, status, timestamp, itemTypeName, user!, [] );

        if ( dataStructure.dataStructure?.[ 0 ] ) {
            const dataObject = dataStructure.dataStructure[ 0 ];

            if ( propsToSet.columns?.length ) {
                propsToSet.columns = remapFieldsForReferenceView( propsToSet.columns, dataObject );
                propsToSet.columns = addParentReferenceAttributeColumnsToColumns( propsToSet.columns!, dataObject );
            }

            if ( propsToSet.rowStyling ) {
                propsToSet.rowStyling = remapFieldsForReferenceView( propsToSet.rowStyling, dataObject ) as RowStyling[];
            }
            if ( propsToSet.fixFilters?.length ) {
                propsToSet.fixFilters = remapFieldsForReferenceView( propsToSet.fixFilters, dataObject ) as ColumnFilter[];
            }
            if ( propsToSet.filters?.length ) {
                propsToSet.filters = remapFieldsForReferenceView( propsToSet.filters, dataObject ) as ColumnFilter[];
            }
            if ( propsToSet.sorters?.length ) {
                propsToSet.sorters = remapFieldsForReferenceView( propsToSet.sorters, dataObject ) as ColumnSorter[];
            }
        }

        this.props.setParams( uid, propsToSet );
    }

    /**
     * Gets the views from user.
     *
     * @param {Schema} schema
     * @param {string} itemTypeName
     * @param {string} parentItemTypeName
     * @param {DataStructure} dataStructure
     * @param {User} user
     */
    getViewsForViewSelectorDropdown( schema : Schema, itemTypeName : string, parentItemTypeName : string, dataStructure : DataStructure, user : User ) {
        return getViews( schema, itemTypeName, parentItemTypeName, dataStructure, user );
    }

    /**
     * Gets the view from given views which match the given panelProps.
     *
     * @param {Schema} schema
     * @param {string} itemTypeName
     * @param {string} parentItemTypeName
     * @param {DataStructure} dataStructure
     * @param {User} user
     * @param {Object} [panelProps=undefined]
     */
    getSelectedViewForViewSelectorDropdown( schema : Schema, itemTypeName : string, parentItemTypeName : string, dataStructure : DataStructure, user : User, panelProps : object | undefined ) {
        const views = this.getViewsForViewSelectorDropdown( schema, itemTypeName, parentItemTypeName, dataStructure, user );

        let retVal : View | undefined;

        if ( panelProps ) {
            for ( const view of views ) {
                if ( viewSelectorPanelPropsCompare( panelProps as ViewSelectorDropdownPanelProps, view.panelProps, dataStructure ) ) {
                    retVal = view;
                    break;
                }
            }
        }

        return retVal;
    }

    /**
     * Memoizable wrapper for getAllColumnsGrouped
     *
     * @private
     */
    getAllColumns( columnDefinitions : ColumnDefinition[], columns : ColumnConfig[], dataStructure : DataStructure, schema : Schema, status : Status, timestamp : string, itemTypeName : string, rawMode : boolean, showAdditionalRawNames : boolean, user : User ) : ColumnSortMenuColumnsGroup[] {
        return getAllColumns( columnDefinitions, columns, dataStructure, schema, status, timestamp, itemTypeName, rawMode, showAdditionalRawNames, user );
    }

    /**
     * Memoizable wrapper for getSortersWithFieldFromSorters
     *
     * @private
     */
    getSortersWithField( sorters : ColumnSorter[] ) : ColumnSortMenuColumnSorter[] {
        return convertSortersToSortersWithField( sorters );
    }

    /**
     * REACT lifecycle method
     */
    render() {
        const {
                  uid,
                  schema,
                  status,
                  timestamp,
                  itemTypeName,
                  parentItemTypeName,
                  dataStructure,
                  columnDefinitions,
                  columns,
                  sorters,
                  showPropertyDefaults,
                  rawMode,
                  showAdditionalRawNames,
                  legend,
                  editMode,
                  editable,
                  showViewSelector,
                  viewSelectorPanelProps,
                  showViewOptionsButton,
                  showColumnOptionsButton,
                  showColumnSortButton,
                  className: toolbarClassName,
                  user
              }                                          = this.props as AllProps,
              layoutComponents : ToolbarLayoutComponents = {
                  left : [],
                  right: []
              };

        // left

        // right
        if ( showViewSelector ) {
            const viewOptions : ViewOption[] = [
                {
                    key  : 'search',
                    type : 'BOOLEAN',
                    label: __( 'YN.base.frontend.dataTable.viewSelector.option.search.label' )
                },
                {
                    key  : 'forceFit',
                    type : 'BOOLEAN',
                    label: __( 'YN.base.frontend.dataTable.viewSelector.option.forceFit.label' )
                }
            ];

            if ( editable ) {
                viewOptions.unshift( {
                    key  : 'editMode',
                    type : 'BOOLEAN',
                    label: __( 'YN.base.frontend.dataTable.viewSelector.option.editMode.label' )
                } );
            }

            layoutComponents.right.push( {
                type  : 'viewSelectorDropdown',
                params: {
                    key: `${uid}_viewSelectorDropdown`,

                    panelKey: uid,

                    module      : getModuleForViewSelector( schema, itemTypeName, parentItemTypeName ),
                    views       : this.getViewsForViewSelectorDropdown( schema, itemTypeName, parentItemTypeName, dataStructure, user! ),
                    selectedView: this.getSelectedViewForViewSelectorDropdown( schema, itemTypeName, parentItemTypeName, dataStructure, user!, viewSelectorPanelProps ),
                    panelProps  : viewSelectorPanelProps,
                    onSetView   : this.onViewSelectorSetView,
                    viewOptions,
                    labeled     : true,

                    style: { width: '18rem' }
                }
            } );
        }

        if ( showViewOptionsButton ) {
            layoutComponents.right.push( {
                type  : 'viewOptionsMenu',
                params: {
                    key: `${uid}_viewOptions`,

                    uid: uid,

                    options           : [
                        {
                            name    : 'editMode',
                            label   : __( 'YN.base.viewOptions.editMode.label' ),
                            value   : editMode,
                            disabled: !editable
                        },
                        {
                            name : 'showPropertyDefaults',
                            label: __( 'YN.base.viewOptions.propertyDefaults.label' ),
                            value: showPropertyDefaults
                        },
                        {
                            name : 'legend',
                            label: __( 'YN.base.viewOptions.legend.label' ),
                            value: legend
                        }
                    ],
                    onViewOptionChange: this.onViewOptionChange
                }
            } );
        }

        if ( showColumnOptionsButton && user ) {
            layoutComponents.right.push( {
                type  : 'columnConfigMenu',
                params: {
                    key: `${uid}_columnConfig`,

                    uid,

                    columns                     : this.getAllColumns( columnDefinitions, columns, dataStructure, schema, status, timestamp, itemTypeName, rawMode, showAdditionalRawNames, user ) as ColumnConfigMenuColumn[],
                    onChange                    : this.onColumnVisibilityChange,
                    onExpandColumnsToHeaderWidth: this.onExpandColumnsToHeaderWidth,
                    onExpandColumnsToScreenWidth: this.onExpandColumnsToScreenWidth
                }
            } );
        }

        if ( showColumnSortButton && user ) {
            layoutComponents.right.push( {
                type  : 'columnSortMenu',
                params: {
                    key: `${uid}_columnsort`,

                    uid,

                    columns : this.getAllColumns( columnDefinitions, columns, dataStructure, schema, status, timestamp, itemTypeName, rawMode, showAdditionalRawNames, user ),
                    sorters : this.getSortersWithField( sorters ),
                    onChange: this.onColumnSortChange
                }
            } );
        }

        const toolbarComponents : ToolbarComponents = getToolbarComponents( layoutComponents );

        return <PrimeToolbar
            left={toolbarComponents.left}
            right={toolbarComponents.right}
            className={`${toolbarClassName} yn-panel-toolbar yn-toolbar-horizontal-separator-bottom`}
        />;
    }
}

/**
 * REDUX stuff
 */
function mapStateToProps( state : StoreState, ownProps : ComponentProps ) : StateProps {
    const retVal : StateProps = {
        user: undefined
    };

    // get some session user information
    if ( state.session?.user ) {
        retVal.user = state.session.user;
    }

    return retVal;
}

/**
 * REDUX stuff
 */
function mapDispatchToProps( dispatch : Dispatch ) {
    return bindActionCreators( Actions, dispatch );
}

export default connect<StateProps, ActionProps, ComponentProps, StoreState>( mapStateToProps, mapDispatchToProps )( Toolbar );
